import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { notification, Tag } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { Modal, Search, Button, DataGrid } from '@hydrodigital/design-system';
import { useNavigate } from 'react-router-dom';
import useDebounce from '@/hooks/useDebounce';

import './index.scss';
import dayjs from 'dayjs';
import ParameterFileService from '../../domain/ParameterFiles/ParameterFilesService';

const parameterFileService = new ParameterFileService();

const UploadParametersTable: FC<{
	isCurrentTab: boolean;
}> = ({ isCurrentTab = false }) => {
	const navigate = useNavigate();
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [showDeleteErrorModal, setShowDeleteErrorModal] = useState(false);
	const [deleteErrorContent, setDeleteErrorContent] = useState<string>('');
	const [searchValue, setSearchValue] = useState<string>('');
	const debouncedSearch = useDebounce(searchValue, 500);
	const [fileSelect, setFileSelect] = useState<any | null>(null);
	const [parameterFiles, setParameterFiles] = useState<any>([]);
	const [paginationState, setPaginationState] = useState({
		pageIndex: 0,
		pageSize: 10,
	});
	const [totalRows, setTotalRows] = useState(0);
	const intervalRef = useRef<any>(null);

	const handleUpdateParameterFiles = useCallback(
		async (loading = true) => {
			const res = await parameterFileService.getParametersFiles({
				name: debouncedSearch || null,
				status: null,
				page: paginationState.pageIndex + 1,
				size: paginationState.pageSize,
				loading,
			});
			setParameterFiles(res.data);
			setTotalRows(res.totalRecords);
		},
		[paginationState, debouncedSearch],
	);

	const gridColumns = [
		{
			header: 'File Name',
			accessorKey: 'name',
			cell: ({ row }: any) => (
				<>
					<span className="table-data-gis">{row.original?.name}</span>
				</>
			),
		},
		{
			header: 'Uploaded by',
			accessorKey: 'uploadBy',
			cell: ({ row }: any) => {
				const uploadBy = row.original?.uploadBy;
				const uploadAt = row.original?.uploadAt;
				return (
					<>
						<div>
							<span className="table-data-gis">{uploadBy}</span>
							<br />
							<span className="date-gis">{dayjs(uploadAt).format('YYYY/MM/DD [at] hh:mm a')}</span>
						</div>
					</>
				);
			},
		},
		{
			header: 'Upload Status',
			accessorKey: 'uploadStatus',
			cell: ({ row }: any) => {
				const status = row.original?.status;
				const fontColor = '#232B36';
				let color = '#FFEECB';
				if (status === 'COMPLETED') color = '#C0EFDE';
				if (status === 'IN PROGRESS') color = '#FFEECB';
				if (status === 'FAILED') color = '#FFD3D3';
				return (
					status && (
						<Tag color={color} style={{ color: fontColor }}>
							{status}
						</Tag>
					)
				);
			},
		},
		{
			header: 'Upload Status Message',
			accessorKey: 'uploadStatusMessage',
			cell: ({ row }: any) => <span className="table-data-gis">{row.original?.statusMessage}</span>,
		},
		{
			id: 'delete-action',
			header: '',
			cell: ({ row }: any) => (
				<span
					style={{ cursor: 'pointer', display: 'flex', justifyContent: 'center' }}
					onClick={() => {
						setFileSelect(row.original);
						setShowDeleteModal(true);
					}}
				>
					<DeleteOutlined style={{ fontSize: '1.1rem', color: '#FF4D4F' }} />
				</span>
			),
		},
	];

	useEffect(() => {
		handleUpdateParameterFiles();
	}, [paginationState.pageIndex, paginationState.pageSize, debouncedSearch]);

	useEffect(() => {
		setPaginationState((prev) => ({ ...prev, pageIndex: 0 }));
	}, [debouncedSearch]);

	useEffect(() => {
		if (isCurrentTab) {
			intervalRef.current = setInterval(() => {
				handleUpdateParameterFiles(false);
			}, 5000);
		}

		return () => {
			clearInterval(intervalRef.current);
		};
	}, [isCurrentTab, paginationState.pageIndex, paginationState.pageSize, debouncedSearch]);

	return (
		<div>
			<div className="flex justify-between">
				<div>
					<Search
						size="small"
						placeholder="Search"
						value={searchValue}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
							setSearchValue(e.target.value);
						}}
						onClear={() => {
							setSearchValue('');
						}}
						showClearButton
					/>
				</div>

				<div>
					<Button
						size="small"
						style={{
							fontWeight: 700,
							fontSize: 14,
							backgroundColor: '#F7F9FA',
							color: '#434B56',
							border: '1px solid #C4CCD5',
						}}
						label="Upload Parameters"
						className="mr-5 boton-inicio flex items-center"
						onClick={() => {
							navigate(`/upload/parameters`);
						}}
					/>
				</div>
			</div>
			<DataGrid
					className="mt-3"
					data={parameterFiles}
					columns={gridColumns}
					pagination={{
						enabled: true,
						manualPagination: true,
						rowCount: totalRows,
						defaultPageSize: 10,
						pageSizeOptions: [10, 20, 50],
					}}
					stickyHeader={true}
					maxHeight="calc(100vh - 320px)"
					paginationState={paginationState}
					onPaginationChange={setPaginationState}
				/>
			<Modal
				isOpen={showDeleteModal}
				size="m"
				style={{ height: '400px' }}
				closeOnOverlayClick={true}
				closeOnEscape={true}
				showCloseButton={true}
				onClose={() => setShowDeleteModal(false)}
			>
				<Modal.Header>
					<span style={{ color: '#232B36', fontSize: '1.2rem', fontWeight: 'bold' }}>
						Warning
					</span>
				</Modal.Header>

				<Modal.Content>
					<span
						style={{
							color: '#74788D',
							fontSize: '1rem',
							fontWeight: '600',
						}}
					>
						Warning: you are about to delete{' '}
						{fileSelect?.totalParams !== undefined
							? `${fileSelect.totalParams} parameters`
							: 'the parameters associated with this bulk upload'}
						. This operation cannot be undone. Do you want to proceed?
					</span>
				</Modal.Content>
				<div className="separator mb-2 mt-2" />

				<Modal.Footer>
					<div className="flex">
						<div className="mr-3">
							<Button
								label="Cancel"
								showIcon={false}
								style={{
									width: '85px',
									height: '40px',
									backgroundColor: '#F7F9FA',
									color: '#434B56',
									border: '1px solid #C4CCD5',
								}}
								onClick={() => setShowDeleteModal(false)}
							/>
						</div>
						<Button
							label="OK"
							showIcon={false}
							style={{
								width: '85px',
								height: '40px',
								backgroundColor: '#00203C',
								color: 'white',
							}}
							onClick={async () => {
								if (fileSelect && fileSelect.id) {
									try {
										await parameterFileService.deleteParameterFile(fileSelect?.id?.toString());
										notification.success({
											message: 'Successful deletion',
											description: 'File deleted successfully',
											placement: 'topRight',
										});
										setShowDeleteModal(false);
										handleUpdateParameterFiles();
									} catch (error: any) {
										const errData = error?.response?.data;
										const msg =
											typeof errData === 'string'
												? errData
												: errData?.message || errData?.body?.message || error?.message || 'An error occurred while deleting the file.';
										setDeleteErrorContent(msg);
										setShowDeleteModal(false);
										setShowDeleteErrorModal(true);
									}
								}
							}}
						/>
					</div>
				</Modal.Footer>
			</Modal>

			<Modal
				isOpen={showDeleteErrorModal}
				size="m"
				closeOnOverlayClick={true}
				closeOnEscape={true}
				showCloseButton={true}
				onClose={() => setShowDeleteErrorModal(false)}
			>
				<Modal.Header>
					<span style={{ color: '#232B36', fontSize: '1.1rem', fontWeight: 'bold' }}>
						Error
					</span>
				</Modal.Header>
				<Modal.Content>
					<pre
						style={{
							whiteSpace: 'pre-wrap',
							wordBreak: 'break-word',
							color: '#74788D',
							fontSize: '0.9rem',
							fontFamily: 'inherit',
							margin: 0,
						}}
					>
						{deleteErrorContent}
					</pre>
				</Modal.Content>
				<div className="separator mb-2 mt-2" />
				<Modal.Footer>
					<Button
						label="Close"
						showIcon={false}
						style={{
							width: '85px',
							height: '40px',
							backgroundColor: '#00203C',
							color: 'white',
						}}
						onClick={() => setShowDeleteErrorModal(false)}
					/>
				</Modal.Footer>
			</Modal>
		</div>
	);
};

export default UploadParametersTable;
